/**
 * شاشة «التوظيف والتدريب» — قمع المتقدمين من التقديم للتعيين.
 *
 * 🔴 **الفجوة اللي بتقفلها:** المتقدمون بيتسجّلوا في شيت، والقمع كله في
 *    دماغ مسؤولة التوظيف. الشيت ما بيقولش «مين مستني مقابلة من إمتى» ولا
 *    «فين وقفت الدفعة» — فالمتقدم بيضيع بين الفلترة والمقابلة ومحدش بيلاحظ.
 *
 * 🔑 **الترتيب بالأقدم انتظارًا** — اللي مستني من أسبوعين هو اللي بيضيع،
 *    مش اللي قدّم النهارده.
 *
 * ⛔ **الأزرار المعروضة جاية من السيرفر** (`next_stages`) — الشاشة ما
 *    بتخترعش المراحل المسموحة. لو اخترعتها، بتوصّل الموظف لزرار بيترفض،
 *    أو بتخفي انتقالًا مسموحًا.
 *
 * ⚠️ **نموذج التقديم مش موجود لسه** — فالفلترة التلقائية مش شغّالة وكل
 *    متقدم بيدخل «محتاج مراجعة». الشاشة بتقول ده صراحةً.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import {
  EmptyState, ErrorState, LoadingState, NoPermissionState,
  NoResultsState, OfflineState,
} from "@/components/states/ScreenStates";
import { SinceAr } from "@/components/RegistryBits";
import { fmtNum } from "@/lib/format";
import { ApiError, errorMessageAr, OfflineError } from "@/api/client";
import {
  convertApplicant, createApplicant, getHiringSummary, listApplicants,
  moveApplicant, STAGE_AR, STAGE_ORDER,
  type ApplicantRow, type ApplicantsMeta, type HiringSummary,
} from "@/api/hiring";
import "@/styles/registry.css";

const PER_PAGE = 15;
const MIN_SEARCH = 2;
const DEBOUNCE_MS = 350;

type State =
  | { kind: "loading" }
  | { kind: "ready"; rows: ApplicantRow[]; meta: ApplicantsMeta }
  | { kind: "error"; messageAr: string }
  | { kind: "offline" };

export function Hiring() {
  const [stage, setStage] = useState<string>("");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [state, setState] = useState<State>({ kind: "loading" });
  const [summary, setSummary] = useState<HiringSummary | null>(null);
  const [denied, setDenied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const deb = useRef<number | null>(null);
  useEffect(() => {
    const t = searchInput.trim();
    const next = t.length >= MIN_SEARCH ? t : "";
    if (deb.current) window.clearTimeout(deb.current);
    deb.current = window.setTimeout(() => setQuery((c) => (c === next ? c : next)), DEBOUNCE_MS);
    return () => { if (deb.current) window.clearTimeout(deb.current); };
  }, [searchInput]);
  useEffect(() => { setPage(1); }, [query, stage]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const [{ rows, meta }, s] = await Promise.all([
        listApplicants({ status: stage || undefined, q: query || undefined,
                         page, perPage: PER_PAGE }),
        getHiringSummary(),
      ]);
      setState({ kind: "ready", rows, meta });
      setSummary(s);
      setDenied(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) { setDenied(true); return; }
      console.error("[hiring] التحميل فشل", err);
      setState(err instanceof OfflineError
        ? { kind: "offline" }
        : { kind: "error", messageAr: errorMessageAr(err) });
    }
  }, [stage, query, page]);
  useEffect(() => { void load(); }, [load]);

  if (denied) {
    return (
      <Shell title="التوظيف والتدريب">
        <NoPermissionState messageAr="التوظيف مش من صلاحيات قسمك" />
      </Shell>
    );
  }

  async function move(a: ApplicantRow, to: string) {
    let reason: string | undefined;
    if (to === "rejected") {
      // ⚠️ السبب إجباري قدّام الموظف برضه مش في السيرفر بس — المرفوض بلا
      //    سبب بيتقدّم تاني بعد شهرين ومحدش يعرف ليه اترفض.
      reason = window.prompt("سبب الرفض (إجباري):") ?? "";
      if (!reason.trim()) { setToast("الرفض محتاج سبب."); return; }
    }
    try {
      const { toastAr } = await moveApplicant(a.id, to, a.version, reason);
      setToast(toastAr); await load();
    } catch (e) { setToast(errorMessageAr(e)); }
  }

  async function convert(a: ApplicantRow) {
    try {
      const { toastAr } = await convertApplicant(a.id, a.version);
      setToast(toastAr); await load();
    } catch (e) { setToast(errorMessageAr(e)); }
  }

  const rows = state.kind === "ready" ? state.rows : [];
  const meta = state.kind === "ready" ? state.meta : null;
  const counts = meta?.stageCounts ?? summary?.stageCounts ?? {};

  return (
    <Shell title="التوظيف والتدريب"
           search={{ value: searchInput, onChange: setSearchInput,
                     placeholder: "ابحث باسم المتقدم أو رقمه…" }}>
      {summary ? (
        <div className="reg-card" style={{ padding: 14 }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ lineHeight: 1.7 }}>
              <div className="t-meta">في القمع دلوقتي</div>
              <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
                <span className="num">{fmtNum(summary.inPipeline)}</span>
              </div>
              <div className="t-meta">من <span className="num">{fmtNum(summary.total)}</span> متقدم</div>
            </div>
            {(["screening", "interview", "training"] as const).map((s) => (
              <div key={s} style={{ lineHeight: 1.7 }}>
                <div className="t-meta">{STAGE_AR[s]}</div>
                <div style={{ fontSize: "1.15rem", fontWeight: 700 }}>
                  <span className="num">{fmtNum(counts[s] ?? 0)}</span>
                </div>
              </div>
            ))}
            <span style={{ flex: 1 }} />
            {/* ⚠️ `null` = النموذج مش موجود — والصفر كان هيكدب */}
            <div style={{ lineHeight: 1.7 }}>
              <div className="t-meta">نموذج التقديم</div>
              <div style={{ fontSize: "1.05rem", fontWeight: 600 }}>
                {summary.formApplications === null ? "— مش موجود لسه"
                  : <span className="num">{fmtNum(summary.formApplications)}</span>}
              </div>
              {summary.formApplications === null ? (
                <div className="t-meta">فالفلترة التلقائية مش شغّالة — كله «محتاج مراجعة»</div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button type="button" className={`filt${stage === "" ? " on" : ""}`}
                onClick={() => setStage("")}>الكل</button>
        {STAGE_ORDER.map((s) => (
          <button key={s} type="button" className={`filt${stage === s ? " on" : ""}`}
                  onClick={() => setStage(s)}>
            {STAGE_AR[s]} · <span className="num">{fmtNum(counts[s] ?? 0)}</span>
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button type="button" className="filt on" onClick={() => setAdding(true)}>
          + سجّل متقدم
        </button>
      </div>

      {toast ? (
        <div className="reg-card" style={{ padding: 10, marginTop: 10 }} role="status">
          {toast} <button type="button" className="filt"
                          onClick={() => setToast(null)}>تمام</button>
        </div>
      ) : null}

      <div className="reg-card" style={{ marginTop: 12 }}>
        <div className="reg-head">
          <span className="sec-title">
            {meta ? <><span className="num">{fmtNum(meta.total)}</span> متقدم</> : "المتقدمون"}
            {query ? ` · بحث عن «${query}»` : ""}
          </span>
          <span className="t-meta">مرتّبين بالأقدم انتظارًا</span>
        </div>

        {state.kind === "loading" ? <LoadingState rows={5} /> : null}
        {state.kind === "offline" ? <OfflineState onRetry={() => void load()} /> : null}
        {state.kind === "error" ? (
          <ErrorState messageAr={state.messageAr} onRetry={() => void load()} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && query ? (
          <NoResultsState query={query} onClear={() => setSearchInput("")} />
        ) : null}
        {state.kind === "ready" && rows.length === 0 && !query ? (
          <EmptyState
            title="لسه مفيش متقدمين"
            hint="سجّل المتقدم اللي وصلك — لحد ما نموذج التقديم يشتغل"
            action={{ label: "+ سجّل متقدم", onClick: () => setAdding(true) }} />
        ) : null}

        {rows.length > 0 ? (
          <div className="reg-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>المتقدم</th><th>المؤهل واللغة</th><th>خبرة أونلاين</th>
                  <th>الفلترة</th><th>المرحلة</th><th>مستني من</th><th>الإجراء</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <bdi style={{ fontWeight: 600 }}>{a.name_ar}</bdi>
                      {a.phone_e164 ? (
                        <div className="t-meta"><bdi className="num">{a.phone_e164}</bdi></div>
                      ) : null}
                      {a.rejection_reason_ar ? (
                        <div className="t-meta">سبب الرفض: <bdi>{a.rejection_reason_ar}</bdi></div>
                      ) : null}
                    </td>
                    <td className="t-desc">
                      <bdi>{a.qualification || "—"}</bdi>
                      {a.language_level ? ` · ${a.language_level}` : ""}
                      {a.has_ijazah_certificate ? " · سند" : ""}
                    </td>
                    <td><span className="num">{fmtNum(a.online_experience_years)}</span> سنة</td>
                    <td className="t-desc">{a.auto_filter_ar}</td>
                    <td className="t-desc">{a.status_ar}</td>
                    <td><SinceAr at={a.waiting_since} /></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {/* ⛔ الأزرار من السيرفر — الشاشة ما بتخترعش المسموح */}
                      {a.next_stages.map((s) => (
                        <button key={s} type="button"
                                className={`filt${s === "rejected" ? "" : " on"}`}
                                style={{ marginInlineEnd: 4 }}
                                onClick={() => void move(a, s)}>
                          {s === "rejected" ? "ارفض" : STAGE_AR[s] || s}
                        </button>
                      ))}
                      {a.status === "hired" && !a.teacher_id ? (
                        <button type="button" className="filt on"
                                onClick={() => void convert(a)}>اعمل سجل معلم</button>
                      ) : null}
                      {a.teacher_id ? <span className="t-meta">اتحوّل لمعلم</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {meta && meta.totalPages > 1 ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 14px" }}>
            <button type="button" className="filt" disabled={meta.page <= 1}
                    onClick={() => setPage(meta.page - 1)}>السابق</button>
            <span className="t-meta">
              صفحة <span className="num">{fmtNum(meta.page)}</span> من{" "}
              <span className="num">{fmtNum(meta.totalPages)}</span>
            </span>
            <button type="button" className="filt" disabled={meta.page >= meta.totalPages}
                    onClick={() => setPage(meta.page + 1)}>التالي</button>
          </div>
        ) : null}
      </div>

      {adding ? (
        <AddDialog onClose={() => setAdding(false)}
                   onDone={async (m) => { setAdding(false); setToast(m); await load(); }} />
      ) : null}
    </Shell>
  );
}

function AddDialog({ onClose, onDone }: {
  onClose: () => void; onDone: (t: string) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [qual, setQual] = useState("");
  const [lang, setLang] = useState("");
  const [years, setYears] = useState("0");
  const [ijazah, setIjazah] = useState(false);
  const [batch, setBatch] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const { toastAr } = await createApplicant({
        name_ar: name.trim(),
        ...(phone.trim() ? { phone_e164: phone.trim() } : {}),
        ...(qual.trim() ? { qualification: qual.trim() } : {}),
        ...(lang.trim() ? { language_level: lang.trim() } : {}),
        has_ijazah_certificate: ijazah,
        online_experience_years: Number(years) || 0,
        ...(batch.trim() ? { batch_id: batch.trim() } : {}),
      });
      await onDone(toastAr);
    } catch (e) { setErr(errorMessageAr(e)); setBusy(false); }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="تسجيل متقدم"
         className="modal-scrim"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="reg-card" style={{ width: "min(520px, 100%)", padding: 16 }}>
        <div className="sec-title">سجّل متقدم</div>
        <div className="t-meta" style={{ lineHeight: 1.9 }}>
          بيدخل مرحلة <strong>«جديد»</strong>، والفلترة عليكي —
          مفيش فلترة تلقائية لحد ما نموذج التقديم يشتغل.
        </div>

        <Field label="الاسم — إجباري" v={name} set={setName} autoFocus />
        <Field label="رقم التليفون" v={phone} set={setPhone} />
        <Field label="المؤهل" v={qual} set={setQual} />
        <Field label="مستوى اللغة (B2 · C1 · C2)" v={lang} set={setLang} />
        <Field label="سنين الخبرة أونلاين" v={years} set={setYears} type="number" />
        <Field label="الدفعة (اختياري)" v={batch} set={setBatch} />

        <label style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
          <input type="checkbox" checked={ijazah}
                 onChange={(e) => setIjazah(e.target.checked)} />
          <span className="t-meta">معاه سند / إجازة قرآنية</span>
        </label>

        {err ? (
          <div className="reg-card" style={{ padding: 10, marginTop: 10 }} role="alert">
            ⚠️ {err}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button type="button" className="filt on" disabled={busy || !name.trim()}
                  onClick={() => void submit()}>سجّل</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="filt" disabled={busy} onClick={onClose}>إلغاء</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, set, type = "text", autoFocus = false }: {
  label: string; v: string; set: (s: string) => void; type?: string; autoFocus?: boolean;
}) {
  return (
    <label style={{ display: "block", marginTop: 10 }}>
      <div className="t-meta">{label}</div>
      <input className="inp" style={{ width: "100%" }} type={type} value={v}
             autoFocus={autoFocus} onChange={(e) => set(e.target.value)} />
    </label>
  );
}
